// Feedback + safety sub-sections for SrDetailPage.
// Extracted to keep SrDetailPage.components.tsx under 200 lines.

import { useState, type JSX } from "react";
import { SwitchToggle } from "../components/SwitchToggle.js";
import { AlertDialog } from "../components/AlertDialog.js";

// ── Family Feedback ────────────────────────────────────────────────────────────

interface FamilyFeedbackProps {
  carerRequestedAgain: boolean;
  carerExcludedByFamily: boolean;
  carerName?: string | null;
  patientRef: string;
  isPending: boolean;
  onPatch: (data: Record<string, unknown>) => void;
}

export function FamilyFeedbackSection({
  carerRequestedAgain,
  carerExcludedByFamily,
  carerName,
  patientRef,
  isPending,
  onPatch,
}: FamilyFeedbackProps): JSX.Element {
  const [pendingExclude, setPendingExclude] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <h2 className="text-sm font-semibold text-slate-700">Family Feedback</h2>

      <SwitchToggle
        label="Family requested this carer again"
        checked={carerRequestedAgain}
        onChange={(v) => onPatch({ carer_requested_again: v })}
      />

      {carerExcludedByFamily ? (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
            Excluded by family
          </span>
        </div>
      ) : (
        <SwitchToggle
          label="Family asked carer NOT to return"
          checked={false}
          onChange={() => setPendingExclude(true)}
        />
      )}

      {pendingExclude && (
        <AlertDialog
          title="Exclude carer from patient?"
          description={`This will permanently prevent ${carerName ?? "this carer"} from being assigned to patient ${patientRef}. Are you sure?`}
          confirmLabel="Exclude"
          isPending={isPending}
          onConfirm={() => {
            onPatch({ carer_excluded_by_family: true });
            setPendingExclude(false);
          }}
          onCancel={() => setPendingExclude(false)}
        />
      )}
    </div>
  );
}

// ── Incident & Safety ──────────────────────────────────────────────────────────

interface IncidentProps {
  incidentDuringAssignment: boolean;
  isPending: boolean;
  onPatch: (data: Record<string, unknown>) => void;
}

export function IncidentSafetySection({
  incidentDuringAssignment,
  isPending,
  onPatch,
}: IncidentProps): JSX.Element {
  const [pendingIncident, setPendingIncident] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-red-100 shadow-sm p-5 space-y-4">
      <h2 className="text-sm font-semibold text-slate-700">Incident &amp; Safety</h2>

      {incidentDuringAssignment ? (
        <p className="text-sm font-medium text-red-600">Incident logged for this assignment.</p>
      ) : (
        <SwitchToggle
          label="Incident occurred during this assignment"
          checked={false}
          onChange={() => setPendingIncident(true)}
        />
      )}

      {pendingIncident && (
        <AlertDialog
          title="Log an incident?"
          description="Turning this on will notify the CS Lead and log an audit event. This cannot be undone."
          confirmLabel="Log incident"
          isPending={isPending}
          onConfirm={() => {
            onPatch({ incident_during_assignment: true });
            setPendingIncident(false);
          }}
          onCancel={() => setPendingIncident(false)}
        />
      )}
    </div>
  );
}
